import {
  InternalError,
  ErrorData,
  IntegrationAppError,
} from '@integration-app/sdk'
import * as Sentry from '@sentry/react'

import { createErrorToast } from 'components/Error/createErrorToast'
import { ENV_CONFIG } from 'config/env'

const errorLevel = ENV_CONFIG.NODE_ENV === 'production' ? 'error' : 'debug'

/**
 * In development mode, React will rethrow errors caught within an error boundary.
 * This will result in errors being reported twice to Sentry with the above setup,
 * but this won’t occur in your production build.
 *
 * https://docs.sentry.io/platforms/javascript/guides/react/features/error-boundary/#:~:text=in%20development%20mode%2C%20react%20will%20rethrow%20errors%20caught%20within%20an%20error%20boundary.
 */

export type ErrorHandlerErrorType = Error | IntegrationAppError
export type ErrorHandlerDataType = ErrorData['data']

export function onErrorHandler(
  error: ErrorHandlerErrorType,
  data?: ErrorHandlerDataType,
) {
  const errorData = createInternalError(error, data)
  captureException(errorData)
}

function createInternalError(
  error: ErrorHandlerErrorType,
  data: ErrorHandlerDataType = {},
) {
  if (error instanceof IntegrationAppError) {
    return error
  }
  return new InternalError({
    message: error.message,
    data: {
      stack: error.stack,
      ...data,
    },
    causedByError: error,
  })
}

function captureException(error: IntegrationAppError) {
  createErrorToast(error)

  if (ENV_CONFIG.ENABLE_TRACKING && !(error instanceof InternalError)) {
    Sentry.captureException(error, {
      level: errorLevel,
    })
  }
}
